
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button} from '@/components/ui/button';
import { Input} from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Mic, MicOff, Send } from 'lucide-react';
import { AudioRecorder, encodeAudioForAPI } from '@/utils/AudioRecorder';
import { ChatMessage, AudioState } from '@/types/chat';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { format, isToday, isYesterday } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DateRange } from 'react-day-picker';
import { Database } from '@/integrations/supabase/types';
import { Json } from '@/integrations/supabase/types';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import Hey from '@/lib/hey-utils';

type ConversationContext = {
  locations: string[];
  dates: { [key: string]: string };
  understanding: { [key: string]: Json };
  lastUpdate: string;
}

type ConversationContextRecord = Database['public']['Tables']['conversation_contexts']['Row'] & {
  context: ConversationContext;
}

const ChatInterface = () => {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [audioState, setAudioState] = useState<AudioState>({
    isRecording: false,
    isProcessing: false,
    isSpeaking: false,
  });
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const audioRecorderRef = useRef<AudioRecorder | null>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const errorMessage = t('assistant.error', 'I have encountered a technical problem while processing your message. Please try again later.');
 
  const { data: messages = [] } = useQuery({
    queryKey: ['messages'],
    queryFn: async () => {
      const { data: messages, error } = await supabase
        .from('messages')
        .select('*')
        .order('timestamp', { ascending: true });
 
      if (error) throw error;
 
      return messages?.map(msg => ({
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: new Date(msg.timestamp).getTime(),
        type: msg.type as 'text' | 'audio'
      })) || [];
    },
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 60, // Keep data in cache for 1 hour
  });
 
  const { data: userId } = useQuery({
    queryKey: ['userId'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id;
    },
  });
 
  const { data: conversationContext } = useQuery<ConversationContextRecord | null>({
    queryKey: ['conversationContext'],
    queryFn: async () => {
      if (!userId) return null;
      
      const { data: existingContext, error: contextError } = await supabase
        .from('conversation_contexts')
        .select('*')
        .eq('profile_id', userId)
        .eq('active', true)
        .maybeSingle();
 
      if (contextError && contextError.code !== 'PGRST116') throw contextError;
 
      if (existingContext) {
        return existingContext as ConversationContextRecord;
      }
 
      const initialContext: ConversationContext = {
        locations: [],
        dates: {},
        understanding: {},
        lastUpdate: new Date().toISOString()
      };
 
      const { data: newContext, error: insertError } = await supabase
        .from('conversation_contexts')
        .insert({
          profile_id: userId,
          context: initialContext as unknown as Json,
          active: true
        })
        .select()
        .single();
 
      if (insertError) throw insertError;
      return newContext as unknown as ConversationContextRecord;
    },
    enabled: !!userId,
  });

  useEffect(() => {
  if (messages.length === 0) {
    const welcomeMessage: ChatMessage = {
      id: 'welcome',
      role: 'assistant',
      content: t('assistant.welcome', "Hello! I'm your digital travel assistant. I'm here to make your journey more enjoyable by assisting you with your travel plans and itineraries. Please don't hesitate to send me the details of your trips — I don't have access to them yet. How can I assist you today? 😊"),
      timestamp: Date.now(),
      type: 'text',
    };
    queryClient.setQueryData(['messages'], (old: ChatMessage[] = []) => [...old, welcomeMessage]);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [messages.length, t, queryClient]);
 
  const updateContext = async (newContext: Partial<ConversationContext>) => {
    if (!userId || !conversationContext) return;
 
    try {
      const updatedContext: ConversationContext = {
        locations: conversationContext.context.locations,
        dates: conversationContext.context.dates,
        understanding: conversationContext.context.understanding,
        lastUpdate: new Date().toISOString(),
        ...newContext
      };
 
      const { error } = await supabase
        .from('conversation_contexts')
        .update({
          context: updatedContext as unknown as Json
        })
        .eq('id', conversationContext.id);
 
      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ['conversationContext'] });
    } catch (error) {
      console.error('Error updating context:', error);
      toast.error(t('assistant.errorUpdatingContext', 'Failed to update conversation context'));
    }
  };
 
  useEffect(() => {
    if (messageEndRef.current) {
      setTimeout(() => {
        messageEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        if (scrollAreaRef.current) {
          scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [messages]);
 
  const persistMessage = async (message: ChatMessage) => {
    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          content: message.content,
          role: message.role,
          type: message.type,
          timestamp: new Date(message.timestamp).toISOString(),
          profile_id: (await supabase.auth.getUser()).data.user?.id
        });
 
      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    } catch (error) {
      console.error('Error persisting message:', error);
      toast.error(t('assistant.errorSavingMessage', 'Failed to save message'));
    }
  };
 
  const handleSendMessage = async (text: string) => {
    if (!text.trim() || !userId) return;
 
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: Date.now(),
      type: 'text',
    };
 
    queryClient.setQueryData(['messages'], (old: ChatMessage[] = []) => [...old, newMessage]);
    setInputText('');
    setDateRange(undefined);
    setIsAiThinking(true);
 
    await persistMessage(newMessage);

    const response = await supabase.functions.invoke('chat', {
      body: {
        message: text,
        userId: userId,
        dates: {
          departure: dateRange?.from?.toISOString(),
          return: dateRange?.to?.toISOString()
        },
        context: conversationContext?.context || {}
      },
    });

    if (response.data.updatedContext) {
      await updateContext(response.data.updatedContext);
    }

    const assistantMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'assistant',
      content: response.data.response,
      timestamp: Date.now(),
      type: 'text',
    };

    queryClient.setQueryData(['messages'], (old: ChatMessage[] = []) => [...old, assistantMessage]);
    await persistMessage(assistantMessage);

    if (response.data.audioEnabled) {
      setAudioState(prev => ({ ...prev, isSpeaking: true }));
      await supabase.functions.invoke('text-to-speech', {
        body: { text: response.data.response },
      });
      setAudioState(prev => ({ ...prev, isSpeaking: false }));
    }
    setIsAiThinking(false);
  };
 
  const handleStartRecording = async () => {
    try {
      audioRecorderRef.current = new AudioRecorder((audioData) => {
        const encodedAudio = encodeAudioForAPI(audioData);
        supabase.functions.invoke('speech-to-text', {
          body: { audio: encodedAudio },
        });
      });
 
      await audioRecorderRef.current.start();
      setAudioState(prev => ({ ...prev, isRecording: true }));
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error(t('assistant.microphoneError', 'Could not access microphone'));
    }
  };
 
  const handleStopRecording = () => {
    if (audioRecorderRef.current) {
      audioRecorderRef.current.stop();
      setAudioState(prev => ({ ...prev, isRecording: false, isProcessing: true }));
    }
  };
 
  const groupedMessages = () => {
    const groups: { [key: string]: ChatMessage[] } = {};
    
    messages.forEach(message => {
      const date = new Date(message.timestamp);
      let key: string;
      
      if (isToday(date)) {
        key = 'Today';
      } else if (isYesterday(date)) {
        key = 'Yesterday';
      } else {
        key = format(date, 'dd-MM-yyyy');
      }
      
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(message);
    });
    
    return groups;
  };

  const handleSendButtonClick = async () => {
    if (!inputText.trim() || audioState.isRecording || audioState.isProcessing) return;

    // 1. Add user message to messages
    const userMessage: ChatMessage = {
      id: Date.now().toString() + Math.random(),
      role: 'user',
      content: inputText,
      timestamp: Date.now(),
      type: 'text',
    };
    queryClient.setQueryData(['messages'], (old: ChatMessage[] = []) => [...old, userMessage]);
    await persistMessage(userMessage);

    setInputText('');

    setIsAiThinking(true);

    // 2. Call the custom endpoint and add assistant message to messages
    const responseText = await callConversationalAssistant({ message: userMessage.content, userId });
    const assistantMessage: ChatMessage = {
      id: Date.now().toString() + Math.random(),
      role: 'assistant',
      content: responseText ?? errorMessage,
      timestamp: Date.now(),
      type: 'text',
    };
    queryClient.setQueryData(['messages'], (old: ChatMessage[] = []) => [...old, assistantMessage]);
    await persistMessage(assistantMessage);

    // add a delay to make the loader visible
    setTimeout(() => setIsAiThinking(false), 500);
  };

  const callConversationalAssistant = async (payload: any): Promise<string | null> => {
    const heyUtils = Hey.HeyUtils.getInstance();
    // use ConversationalService to call the custom endpoint
    try { 
      return await heyUtils.getConversationalResponse(payload);
    } catch (error) {
      console.error('Error calling conversational api endpoint:', error);
      return errorMessage;
    }
  };

  function renderWithMarkdown(text: string | undefined) {
    if (typeof text === 'string' && text != errorMessage) {
      return <div
        className="markdown-content"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(text) as string) }}
      />;
    }
    return <div className="text-red-500">{text}</div>;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)] max-w-3xl mx-auto">
      <ScrollArea className="flex-1 p-4 border rounded-lg mb-4" ref={scrollAreaRef}>
        <div className="space-y-6">
          {Object.entries(groupedMessages()).map(([date, dateMessages]) => (
            <div key={date} className="space-y-4">
              <div className="sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-10">
                <h3 className="text-sm font-medium text-muted-foreground py-2 text-center">
                  {date}
                </h3>
              </div>
              {dateMessages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    "flex w-full",
                    message.role === 'assistant' ? 'justify-start' : 'justify-end'
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[80%] rounded-lg p-3",
                      message.role === 'assistant'
                        ? "bg-secondary text-secondary-foreground"
                        : "bg-primary text-primary-foreground"
                    )}
                  >
                    <div className="space-y-1 markdown-content">
                      {message.role === 'assistant' && typeof message.content === 'string'
                        ? renderWithMarkdown(message?.content)
                        : message.content}
                      <div className="text-xs opacity-50">
                        {format(new Date(message.timestamp), 'HH:mm')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
          {isAiThinking && (
            <div className="flex justify-start w-full">
              <div className="bg-secondary text-secondary-foreground rounded-lg p-3 max-w-[80%] flex justify-center items-center">
                <span className="text-3xl font-bold text-center w-full flex justify-center items-center">
                  <span className="animate-bounce inline-block" style={{ animationDelay: '0s' }}>.</span>
                  <span className="animate-bounce inline-block" style={{ animationDelay: '0.2s' }}>.</span>
                  <span className="animate-bounce inline-block" style={{ animationDelay: '0.4s' }}>.</span>
                </span>
              </div>
            </div>
          )}
          <div ref={messageEndRef} />
        </div>
      </ScrollArea>
      
      <div className="flex gap-2 items-center">
        <Button
          variant={audioState.isRecording ? "destructive" : "outline"}
          size="icon"
          onClick={audioState.isRecording ? handleStopRecording : handleStartRecording}
          disabled={audioState.isProcessing}
        >
          {audioState.isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
        
        <Input
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSendButtonClick();
            }
          }}
          placeholder={t('assistant.inputPlaceholder', 'Type your message...')}
          disabled={audioState.isRecording || audioState.isProcessing}
        />
        
        <Button
          onClick={handleSendButtonClick}
          disabled={!inputText.trim() || audioState.isRecording || audioState.isProcessing}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default ChatInterface;
